/**
 * @description Utilities for HLColorPicker event normalization.
 * Highrise HLColorPicker swatch CRUD events may be emitted either as a single payload object
 * (integration style) or as positional args (per docs).
 *
 * Reference: [Highrise Color Picker emits](https://highrise.gohighlevel.com/components/data-input/color-picker)
 */

/** Section key for the brand-board palette swatches — set on the section and matched on select. */
export const BRAND_SWATCH_SECTION = "brand";

export interface NormalizedSwatchCrudPayload {
  /** Section key like 'default' | 'custom' | 'brand' */
  sectionKey?: string;
  /** Section index in the sections array */
  sectionIndex?: number;
  /** Edit/delete index (when available) */
  index?: number;
  /** Swatch object/value (when available) */
  swatch?: unknown;
  /** Value (string or swatch object) (when available) */
  value?: unknown;
}

export type ColorPickerSelectPayload =
  | string
  | {
      inputType?: string;
      value?: string;
      value8?: string;
      custom?: string;
    }
  | null
  | undefined;

export type ColorPickerUpdatePayload =
  | {
      inputType?: string;
      value?: string;
      value8?: string;
      custom?: string;
    }
  | null
  | undefined;

const isCustomInputType = (inputType?: unknown): boolean =>
  String(inputType ?? "").toLowerCase() === "custom";

const getCustomToken = (value?: unknown): string | undefined => {
  const token = typeof value === "string" ? value.trim() : "";
  return token ? token : undefined;
};

/**
 * @description Shared popover styles to keep the color picker within the viewport.
 */
export const COLOR_PICKER_POPOVER_STYLE = {
  maxHeight: "calc(100vh - 140px)",
  overflowY: "auto",
} as const;

/**
 * @description Normalize HLColorPicker swatch CRUD emits to a single payload object.
 *
 * @param args - Raw arguments emitted by HLColorPicker.
 * @returns Normalized payload object for store actions.
 *
 * Logic flow:
 * 1. If the first argument is a plain object, treat it as an already-normalized payload.
 * 2. Otherwise, map positional args into { sectionKey, sectionIndex, index, swatch, value }.
 */
export const normalizeColorPickerSwatchEventArgs = (
  ...args: Array<unknown>
): NormalizedSwatchCrudPayload => {
  const [arg0, arg1, arg2, arg3] = args;

  // Payload-object style
  if (arg0 && typeof arg0 === "object" && !Array.isArray(arg0)) {
    return arg0 as NormalizedSwatchCrudPayload;
  }

  // Positional style
  return {
    sectionKey: typeof arg0 === "string" ? arg0 : undefined,
    sectionIndex: typeof arg1 === "number" ? arg1 : undefined,
    index: typeof arg2 === "number" ? arg2 : undefined,
    swatch: arg3 ?? arg2,
    value: arg3 ?? arg2,
  };
};

/**
 * @description Normalize HLColorPicker select payloads to a single hex value.
 *
 * @param payload - Raw select payload from HLColorPicker.
 * @returns Hex string (with or without '#') or undefined when unavailable.
 */
export const getColorPickerSelectValue = (
  payload: ColorPickerSelectPayload
): string | undefined => {
  if (!payload) {
    return undefined;
  }
  if (typeof payload === "string") {
    return payload;
  }
  if (typeof payload === "object") {
    const { inputType, value, value8, custom } = payload;
    if (isCustomInputType(inputType)) {
      return getCustomToken(custom) || value || value8;
    }
    if (inputType === "HEX") {
      return value8 || value;
    }
    return value || value8;
  }
  return undefined;
};

/**
 * @description Normalize HLColorPicker update emits to a single hex value.
 *
 * HLColorPicker may emit update payloads as positional args:
 * `update:picker(value, value8, alpha, inputType, label, custom, isEdit)`
 * or as an object payload in wrapper integrations.
 *
 * @param args - Raw update event arguments emitted by HLColorPicker.
 * @returns Hex string (with or without '#') or undefined when unavailable.
 */
export const getColorPickerUpdateValue = (...args: Array<unknown>): string | undefined => {
  const [arg0, arg1, , inputType, , custom] = args;

  if (isCustomInputType(inputType)) {
    return getCustomToken(custom) || (typeof arg1 === "string" ? arg1 : undefined);
  }

  if (typeof arg1 === "string") {
    return arg1;
  }

  if (!arg0) {
    return undefined;
  }

  if (typeof arg0 === "string") {
    return arg0;
  }

  if (typeof arg0 === "object" && !Array.isArray(arg0)) {
    const payload = arg0 as ColorPickerUpdatePayload;
    const value = typeof payload?.value === "string" ? payload.value : undefined;
    const value8 = typeof payload?.value8 === "string" ? payload.value8 : undefined;
    if (isCustomInputType(payload?.inputType)) {
      return getCustomToken(payload?.custom) ?? value8 ?? value;
    }
    return value8 ?? value;
  }

  return undefined;
};

/**
 * @description Extract a hex value from a normalized swatch CRUD payload.
 *
 * @param payload - Normalized swatch CRUD payload from HLColorPicker.
 * @returns Hex string (with or without '#') or undefined when unavailable.
 */
export const getColorPickerSwatchValue = (
  payload?: NormalizedSwatchCrudPayload
): string | undefined => {
  if (!payload) {
    return undefined;
  }
  const raw = payload.swatch ?? payload.value ?? payload;
  if (typeof raw === "string") {
    return raw;
  }
  if (raw && typeof raw === "object") {
    const swatch = raw as Record<string, unknown>;
    if (typeof swatch.value8 === "string") {
      return swatch.value8;
    }
    if (typeof swatch.value === "string") {
      return swatch.value;
    }
  }
  return undefined;
};
